// 請われぬ介錯・地割り (exec_maulQuake) の床の演出 — 3案 (a/b/c) と旧 (old) を実行時に切り替える。
//
// 🔵 2026-09-03 第5便 ユーザー「床技のデザインだけ、警告のデザインも含めてダサい。3案出して」。
//   旧 (render.ts の hz.kind === 'quake' 分岐) は残し、`?quake=old` / `__setQuakeLook('old')` で戻せる。
//   判定・秒数は一切変えない (ここは描くだけ)。
//
// 3案に共通の骨格 (旧との最大の違い):
//   ① 突き立てる前 (0〜slamAt) は「亀裂」を描かない。まだ何も床を打っていない。危険な帯は
//      "床の下で何かが起きている" 現象 (熱・軋み・呪いの滲み) で示す。
//   ② 突き立てた瞬間 (slamAt) から、亀裂が打点から外へ走る。帯の外縁へ届いた瞬間にその帯が噴く
//      (= 実際の噴出時刻 telegraphMs と一致するよう速度を合わせている: 110px + 0.4px/ms)。
//   ③ 噴出は帯ごとに材質が違う: a=灼熱の地脈 (溶けた光の舌) / b=石牙 (石の尖塔) / c=呪いの噴出 (赤黒い煙柱)。
//   赤 = 弾けない攻撃の共通言語 (裁定C) は3案とも保つ。
//
// 🔵 2026-09-03 第6便 ユーザー裁定: **a 灼熱の地脈を採用**。メモ「線っぽいエフェクトは基本ダサい。そこだけ修正」
//   → 線 (stroke) で描いていた要素を面と粒に置き換えた: 亀裂＝細い光の線でなく「暗い裂け目の面 + 溶けた光の溜まり (粒)」、
//     予兆の継ぎ目の光＝縦の短い線でなく「床に寝た光の滲み (面)」。b/c は候補として残置 (未採用・線の要素もそのまま)。
import type { ActiveHit } from './types'
import { ARENA } from './balance'
import { emberBurst, emberRise, glowMote, hash01 } from './fx_kit'
import { crackBitSprite, crackReady, crackSprite, drawMat, dustReady, dustSprite, preloadExecFxMats, tinted } from './exec_fx_mats'

export type QuakeLook = 'old' | 'a' | 'b' | 'c'

// 地割りの赤い筋 (10-29・作者の答え exec-fx-q5「ひびにする」): 滲む光の帯・黒い裂け目・光の粒 → Codex の描いたひびの絵。
//   10-30 作者の答え「入れる」でひびの絵 (床の描いた警告も) が配布版の既定。前の版 (光の筋) は DEV `?execCrack=glow`。
export type ExecCrackLook = 'crack' | 'glow'
let crackLook: ExecCrackLook = 'crack'
export function setExecCrackLook(v: string | null): void { crackLook = v === 'glow' ? 'glow' : 'crack' }
export function getExecCrackLook(): ExecCrackLook { return crackLook }
// ひびの絵1枚を床に敷く長さ (打点から 110px の安置の縁を起点に、外へ繰り返す)。高さは床に寝た薄さ。
const CRACK_TILE_PX = 300
const CRACK_H_PX = 34  // 絵 (512x61) の縦横比どおりなら 36。床に少し寝かせる

// 既定 = a (Claude の推奨)。ユーザー裁定で差し替える。
let quakeLook: QuakeLook = 'a'
export function setQuakeLook(look: QuakeLook): void { quakeLook = look }
export function getQuakeLook(): QuakeLook { return quakeLook }

// 呪いの赤 (render.ts の CURSE_* と同値)。
const CURSE_DEEP = 'rgb(124,11,20)'
const CURSE_MID = 'rgb(176,24,34)'
const CURSE_BRIGHT = 'rgb(225,58,58)'
const MOLTEN = 'rgb(255,138,74)'      // 溶けた石の芯 (橙)
const WHITE_HOT = 'rgb(255,214,180)'  // 白熱 (極小面積)

// 亀裂の走る速さ: 打点から 110px (安置の縁) を起点に 0.4px/ms。
//   帯 k (offset 150+80k・幅80) の外縁 = 190+80k に届く時刻 = slamAt + 200(k+1) = その帯の噴出時刻。
const FISSURE_START_PX = 110
const FISSURE_SPEED_PX_PER_MS = 0.4

type Band = {
  xStart: number
  xEnd: number
  cx: number
  w: number
  floorY: number
  impactX: number
  dir: 1 | -1        // 打点 → 帯 の向き
  inner: number      // 打点に近い側の縁
  outer: number      // 遠い側の縁
  seed: number
  slamAtMs: number   // 突き立てる時刻 (hazard の elapsed 基準)
}

export type QuakeFxContext = {
  // 刃が床を打つ位置 (飛び退いた後の boss.x)。render 側が originX − originDir·telegraphBackstepPx で作る。
  impactX: number
}

function bandOf(hz: ActiveHit, fx: QuakeFxContext): Band {
  const floorY = ARENA.floorY
  const cx = (hz.xStart + hz.xEnd) / 2
  const w = hz.xEnd - hz.xStart
  const dir: 1 | -1 = cx >= fx.impactX ? 1 : -1
  const inner = dir > 0 ? hz.xStart : hz.xEnd
  const outer = dir > 0 ? hz.xEnd : hz.xStart
  // 帯の番号 (0..3) から突き立て時刻を逆算する (譜面の短縮拍で telegraphMs がずれても追従する)。
  const ring = Math.max(0, Math.round((Math.abs(cx - fx.impactX) - 150) / 80))
  const slamAtMs = hz.telegraphMs - 200 * (ring + 1)
  return { xStart: hz.xStart, xEnd: hz.xEnd, cx, w, floorY, impactX: fx.impactX, dir, inner, outer, seed: hz.xStart * 0.037 + hz.id * 1.7, slamAtMs }
}

function clamp01(v: number): number { return v < 0 ? 0 : v > 1 ? 1 : v }
function easeOut(p: number): number { const q = 1 - clamp01(p); return 1 - q * q }
function easeIn(p: number): number { const q = clamp01(p); return q * q }

// 亀裂の折れ線 (帯の中の fromX→toX)。y は床のわずか上で、ぎざぎざは帯ごとに固定。
function traceFissure(ctx: CanvasRenderingContext2D, b: Band, fromX: number, toX: number, amp: number): void {
  const n = 10
  ctx.beginPath()
  for (let i = 0; i <= n; i += 1) {
    const u = i / n
    const x = fromX + (toX - fromX) * u
    // ぎざぎざは帯の絶対座標で決める (伸びている途中で形が変わらない)
    const jag = (hash01(b.seed + Math.round(x * 0.5) * 3.17) - 0.5) * amp
    const y = b.floorY - 2 - Math.abs(jag)
    if (i === 0) ctx.moveTo(x, y)
    else ctx.lineTo(x, y)
  }
}

// 亀裂を「面」として描くための多角形 (上縁と下縁を別のぎざぎざで結ぶ)。幅 2〜8px が場所ごとに変わる。
function fissureGapPath(ctx: CanvasRenderingContext2D, b: Band, fromX: number, toX: number): void {
  const n = 12
  const top: Array<[number, number]> = []
  const bottom: Array<[number, number]> = []
  for (let i = 0; i <= n; i += 1) {
    const u = i / n
    const x = fromX + (toX - fromX) * u
    const k = Math.round(x * 0.5)
    const jag = (hash01(b.seed + k * 3.17) - 0.5) * 6
    const half = (i === 0 || i === n) ? 0.6 : 1.2 + hash01(b.seed + k * 1.31) * 3.2
    const yc = b.floorY - 2 - Math.abs(jag) * 0.5
    top.push([x, yc - half])
    bottom.push([x + (hash01(b.seed + k * 0.77) - 0.5) * 3, yc + half])
  }
  ctx.beginPath()
  top.forEach(([x, y], i) => { if (i === 0) ctx.moveTo(x, y); else ctx.lineTo(x, y) })
  for (let i = bottom.length - 1; i >= 0; i -= 1) ctx.lineTo(bottom[i]![0], bottom[i]![1])
  ctx.closePath()
}

// 打点から走る亀裂。帯の中で「もう届いた区間」だけを描く。style で材質を変える。
function drawRacingFissure(
  ctx: CanvasRenderingContext2D, b: Band, elapsedMs: number,
  style: { glow: number; hot: boolean; smoke: boolean; timeSec: number },
): { covered: number; tipX: number | null } {
  const since = elapsedMs - b.slamAtMs
  if (since < 0) return { covered: 0, tipX: null }
  const frontDist = FISSURE_START_PX + since * FISSURE_SPEED_PX_PER_MS
  const frontX = b.impactX + b.dir * frontDist
  const innerDist = Math.abs(b.inner - b.impactX)
  if (frontDist <= innerDist + 1) return { covered: 0, tipX: null }
  const reached = b.dir > 0 ? Math.min(b.outer, frontX) : Math.max(b.outer, frontX)
  const covered = clamp01(Math.abs(reached - b.inner) / Math.max(1, b.w))
  const tipInside = Math.abs(frontX - b.impactX) < Math.abs(b.outer - b.impactX)

  if (crackLook === 'crack') preloadExecFxMats()
  if (crackLook === 'crack' && crackReady()) {
    drawCrackStrip(ctx, b, reached, since, style)
    return { covered, tipX: tipInside ? frontX : null }
  }

  ctx.save()
  // 下から漏れる面の光 (帯の幅に収める・ぼかした太い帯＝線ではなく滲み)
  ctx.save()
  ctx.beginPath()
  ctx.rect(b.xStart - 2, b.floorY - 30, b.w + 4, 34)
  ctx.clip()
  ctx.globalCompositeOperation = 'lighter'
  ctx.lineCap = 'round'
  ctx.lineJoin = 'round'
  ctx.globalAlpha = 0.20 * style.glow
  ctx.strokeStyle = CURSE_DEEP
  ctx.lineWidth = 20
  traceFissure(ctx, b, b.inner, reached, 6)
  ctx.stroke()
  ctx.restore()
  // 割れ目＝暗い裂け目の「面」。幅が場所ごとに違う多角形で、線に見せない。
  ctx.globalCompositeOperation = 'source-over'
  ctx.globalAlpha = 0.94
  ctx.fillStyle = '#050507'
  fissureGapPath(ctx, b, b.inner, reached)
  ctx.fill()
  // 裂け目の中の溶けた光＝線でなく、裂け目に沿った光の溜まり (粒)。
  const segs = Math.max(1, Math.round(Math.abs(reached - b.inner) / 11))
  for (let i = 0; i < segs; i += 1) {
    const u = (i + 0.5) / segs
    const x = b.inner + (reached - b.inner) * u
    const jag = (hash01(b.seed + Math.round(x * 0.5) * 3.17) - 0.5) * 6
    const y = b.floorY - 2 - Math.abs(jag) * 0.5
    const flicker = 0.75 + 0.25 * Math.sin(style.timeSec * 11 + i * 1.9 + b.seed)
    const r = 3.2 + hash01(b.seed + i * 2.3) * 2.6
    glowMote(ctx, x, y, r * 1.8, CURSE_MID, 0.30 * style.glow * flicker)
    if (style.hot) glowMote(ctx, x, y, r, MOLTEN, 0.42 * style.glow * flicker)
    else glowMote(ctx, x, y, r * 0.9, CURSE_BRIGHT, 0.32 * style.glow * flicker)
  }
  // 先端: 走っている最中だけ白熱の粒
  if (tipInside) {
    if (style.hot) {
      glowMote(ctx, frontX, b.floorY - 3, 9, MOLTEN, 0.7 * style.glow)
      glowMote(ctx, frontX, b.floorY - 3, 3.5, WHITE_HOT, 0.8 * style.glow)
    } else {
      glowMote(ctx, frontX, b.floorY - 3, 7, CURSE_BRIGHT, 0.6 * style.glow)
    }
  }
  // 煙: 届いた区間から赤黒い靄が立つ (c 案)
  if (style.smoke) {
    ctx.globalCompositeOperation = 'source-over'
    for (let i = 0; i < 5; i += 1) {
      const u = (i + 0.5) / 5
      if (u > covered) break
      const x = b.inner + b.dir * b.w * u
      const life = (style.timeSec * 0.9 + hash01(b.seed + i * 4.3)) % 1
      const y = b.floorY - 4 - life * 20
      const r = 5 + life * 7
      ctx.globalAlpha = 0.35 * (1 - life)
      ctx.fillStyle = 'rgb(40,6,12)'
      ctx.beginPath()
      ctx.arc(x, y, r, 0, Math.PI * 2)
      ctx.fill()
    }
  }
  ctx.restore()
  return { covered, tipX: tipInside ? frontX : null }
}

// ひびの絵を、帯の中で「もう届いた区間」だけ見せる。絵は打点の側から外へ、床の座標に固定して敷く
//   (帯ごとに絵の続きが出る・伸びている途中で形が動かない)。裂け目の暗さ = 同じ絵の黒い写しを下に敷く。
//   絵は幅いっぱいに伸びきった段3だけを使う (段0〜2 は絵の左だけの短いひびで、敷くと途切れる)。
//   走っている間は熱を抑え、帯の外縁まで届いたら強く光る。先端には砕けた石の粒と小さな熱。
function drawCrackStrip(
  ctx: CanvasRenderingContext2D, b: Band, reached: number, since: number,
  style: { glow: number; hot: boolean; timeSec: number },
): void {
  const startX = b.impactX + b.dir * FISSURE_START_PX
  const innerDist = Math.abs(b.inner - b.impactX)
  const outerDist = Math.abs(b.outer - b.impactX)
  // 帯の外縁まで届いてから開く (帯の噴き上がりの合図と同じ拍)。
  const openAt = (outerDist - FISSURE_START_PX) / FISSURE_SPEED_PX_PER_MS
  const open = clamp01((since - openAt) / 160)
  const heat = 0.55 + 0.45 * open
  const img = crackSprite(3)
  if (!img) return
  ctx.save()
  ctx.beginPath()
  ctx.rect(Math.min(b.inner, reached), b.floorY - CRACK_H_PX * 1.5, Math.abs(reached - b.inner), CRACK_H_PX * 2.5)
  ctx.clip()
  const firstTile = Math.floor((innerDist - FISSURE_START_PX) / CRACK_TILE_PX)
  const lastTile = Math.floor((outerDist - FISSURE_START_PX) / CRACK_TILE_PX)
  const flip = b.dir < 0
  const pulse = 0.85 + 0.15 * Math.sin(style.timeSec * 6 + b.seed)
  for (let k = Math.max(0, firstTile); k <= lastTile; k += 1) {
    const x0 = startX + b.dir * k * CRACK_TILE_PX
    const y = b.floorY + 1
    drawMat(ctx, tinted(img, '5,5,7'), x0, y, CRACK_TILE_PX, CRACK_H_PX * 1.15, 0, 0.5, 0.9, 0, flip)
    // 予兆の描いた警告 (glow < 0.5) では光を足さず、沈んだ赤をふつうに重ねる (10-30「ネオン的」)。
    if (style.glow < 0.5) drawMat(ctx, tinted(img, '120,18,26'), x0, y, CRACK_TILE_PX, CRACK_H_PX, 0, 0.5, 0.8 * heat, 0, flip)
    else drawMat(ctx, img, x0, y, CRACK_TILE_PX, CRACK_H_PX, 0, 0.5, style.glow * pulse * heat, 0, flip, 'lighter')
  }
  ctx.restore()
  // 先端: 走っている間だけ、砕けた石の粒と小さな熱。
  const front = b.impactX + b.dir * (FISSURE_START_PX + since * FISSURE_SPEED_PX_PER_MS)
  if (Math.abs(front - b.impactX) < outerDist) {
    const bit = crackBitSprite(Math.floor(style.timeSec * 14 + b.seed))
    if (bit) drawMat(ctx, bit, front, b.floorY + 2, 34, 34 * bit.naturalHeight / bit.naturalWidth, 0.5, 1, 0.9 * style.glow, 0, flip)
    if (style.glow >= 0.5) glowMote(ctx, front, b.floorY - 3, 8, style.hot ? MOLTEN : CURSE_BRIGHT, 0.45 * style.glow)
  }
}

// 床に寝た楕円の面 (遠近で潰した光/影)。
function floorEllipse(ctx: CanvasRenderingContext2D, cx: number, cy: number, rx: number, ry: number, color: string, a: number, mode: GlobalCompositeOperation): void {
  if (a <= 0.005) return
  ctx.save()
  ctx.globalCompositeOperation = mode
  ctx.globalAlpha = a
  ctx.translate(cx, cy)
  ctx.scale(1, ry / rx)
  const g = ctx.createRadialGradient(0, 0, 0, 0, 0, rx)
  g.addColorStop(0, color)
  g.addColorStop(1, 'rgba(0,0,0,0)')
  ctx.fillStyle = g
  ctx.beginPath()
  ctx.arc(0, 0, rx, 0, Math.PI * 2)
  ctx.fill()
  ctx.restore()
}

// 石畳の帯を4枚の板に割って描く (b 案の土台・a/c も薄く使う)。lift = 中央側の縁が浮く高さ。
function drawSlabs(ctx: CanvasRenderingContext2D, b: Band, lift: number, alpha: number): void {
  if (alpha <= 0.01) return
  ctx.save()
  ctx.globalCompositeOperation = 'source-over'
  const n = 4
  for (let i = 0; i < n; i += 1) {
    const x0 = b.xStart + b.w * (i / n)
    const x1 = b.xStart + b.w * ((i + 1) / n)
    const hL = lift * (0.4 + 0.6 * hash01(b.seed + i * 5.7))
    const hR = lift * (0.4 + 0.6 * hash01(b.seed + i * 5.7 + 1.9))
    ctx.globalAlpha = alpha
    ctx.fillStyle = '#26242a'
    ctx.beginPath()
    ctx.moveTo(x0, b.floorY + 1)
    ctx.lineTo(x0 + 1, b.floorY - hL)
    ctx.lineTo(x1 - 1, b.floorY - hR)
    ctx.lineTo(x1, b.floorY + 1)
    ctx.closePath()
    ctx.fill()
    ctx.globalAlpha = alpha * 0.8
    ctx.strokeStyle = '#6f6960'
    ctx.lineWidth = 1
    ctx.beginPath()
    ctx.moveTo(x0 + 1, b.floorY - hL)
    ctx.lineTo(x1 - 1, b.floorY - hR)
    ctx.stroke()
    // 継ぎ目の暗線
    ctx.globalAlpha = alpha * 0.9
    ctx.strokeStyle = '#0a0a0c'
    ctx.lineWidth = 1.4
    ctx.beginPath()
    ctx.moveTo(x1, b.floorY + 1)
    ctx.lineTo(x1 - 1, b.floorY - Math.max(hL, hR))
    ctx.stroke()
  }
  ctx.restore()
}

// 継ぎ目から漏れる赤い光 (帯の中の4本の継ぎ目に沿った短い縦線 + 床面の滲み)。
function drawSeamGlow(ctx: CanvasRenderingContext2D, b: Band, a: number, timeSec: number, height: number): void {
  if (a <= 0.01) return
  ctx.save()
  ctx.globalCompositeOperation = 'lighter'
  ctx.lineCap = 'round'
  for (let i = 1; i < 4; i += 1) {
    const x = b.xStart + b.w * (i / 4)
    const flicker = 0.7 + 0.3 * Math.sin(timeSec * 9 + i * 2.1 + b.seed)
    ctx.globalAlpha = a * flicker
    ctx.strokeStyle = CURSE_MID
    ctx.lineWidth = 2.4
    ctx.beginPath()
    ctx.moveTo(x, b.floorY + 1)
    ctx.lineTo(x + (hash01(b.seed + i) - 0.5) * 3, b.floorY - height)
    ctx.stroke()
    ctx.globalAlpha = a * 0.7 * flicker
    ctx.strokeStyle = CURSE_BRIGHT
    ctx.lineWidth = 0.9
    ctx.stroke()
  }
  ctx.restore()
}

// 継ぎ目から滲む光 (面)。線を引かず、床に寝た小さな光の溜まりを継ぎ目の位置に置く (a 案・第6便「線っぽいのはダサい」)。
function drawSeamBloom(ctx: CanvasRenderingContext2D, b: Band, a: number, timeSec: number, size: number): void {
  if (a <= 0.01) return
  for (let i = 1; i < 4; i += 1) {
    const x = b.xStart + b.w * (i / 4) + (hash01(b.seed + i * 1.7) - 0.5) * 6
    const flicker = 0.7 + 0.3 * Math.sin(timeSec * 7 + i * 2.1 + b.seed)
    floorEllipse(ctx, x, b.floorY - 1, size * 1.6, size * 0.55, 'rgba(176,24,34,1)', a * 0.8 * flicker, 'lighter')
    floorEllipse(ctx, x, b.floorY - 1.5, size * 0.8, size * 0.35, 'rgba(255,138,74,1)', a * 0.55 * flicker, 'lighter')
  }
}

// 小石が跳ねる (床が震えている合図)。
function drawHoppingPebbles(ctx: CanvasRenderingContext2D, b: Band, strength: number, timeSec: number, count: number): void {
  if (strength <= 0.01) return
  ctx.save()
  ctx.globalCompositeOperation = 'source-over'
  ctx.fillStyle = '#8a847b'
  for (let i = 0; i < count; i += 1) {
    const x = b.xStart + 6 + hash01(b.seed + i * 3.3) * (b.w - 12)
    const phase = timeSec * (7 + hash01(b.seed + i) * 5) + i * 1.7
    const hop = Math.max(0, Math.sin(phase)) * 5 * strength
    const r = 0.9 + hash01(b.seed + i * 1.1) * 1.3
    ctx.globalAlpha = 0.55 + 0.35 * strength
    ctx.beginPath()
    ctx.arc(x, b.floorY - 1.5 - hop, r, 0, Math.PI * 2)
    ctx.fill()
  }
  ctx.restore()
}

// 継ぎ目から染み出す土埃 (柔らかい灰の粒が上へ抜けて消える)。
function drawDustSeep(ctx: CanvasRenderingContext2D, b: Band, strength: number, timeSec: number, height: number): void {
  if (strength <= 0.01) return
  ctx.save()
  ctx.globalCompositeOperation = 'source-over'
  for (let i = 0; i < 6; i += 1) {
    const life = (timeSec * (0.5 + hash01(b.seed + i * 2.9) * 0.4) + hash01(b.seed + i)) % 1
    const x = b.xStart + b.w * ((i + 0.5) / 6) + (life - 0.5) * 6
    const y = b.floorY - 2 - life * height
    const r = 4 + life * 7
    ctx.globalAlpha = strength * 0.28 * (1 - life)
    const g = ctx.createRadialGradient(x, y, 0, x, y, r)
    g.addColorStop(0, 'rgba(150,144,136,0.9)')
    g.addColorStop(1, 'rgba(110,106,100,0)')
    ctx.fillStyle = g
    ctx.beginPath()
    ctx.arc(x, y, r, 0, Math.PI * 2)
    ctx.fill()
  }
  ctx.restore()
}

// 角のある石片を放射する (噴出の瞬間)。
function drawShards(ctx: CanvasRenderingContext2D, b: Band, p: number, count: number, height: number, colorA: string, colorB: string): void {
  if (p <= 0 || p >= 1) return
  const lift = Math.sin(Math.PI * p)
  ctx.save()
  ctx.globalCompositeOperation = 'source-over'
  ctx.globalAlpha = 0.95 * (1 - p * 0.6)
  for (let i = 0; i < count; i += 1) {
    const seed = b.seed + i * 7.31
    const side = i % 2 === 0 ? 1 : -1
    const startX = b.cx + (hash01(seed) - 0.5) * b.w * 0.4
    const lateral = side * (14 + hash01(seed + 1) * 40)
    const px = startX + lateral * p
    const py = b.floorY - 6 - lift * (height * (0.6 + hash01(seed + 2) * 0.6)) + p * 10
    const size = 3 + hash01(seed + 3) * 4.5
    ctx.save()
    ctx.translate(px, py)
    ctx.rotate((hash01(seed + 4) - 0.5) * 1.2 + side * p * 2.4)
    ctx.fillStyle = i % 3 === 0 ? colorA : colorB
    ctx.beginPath()
    ctx.moveTo(-size, -size * 0.35)
    ctx.lineTo(-size * 0.15, -size)
    ctx.lineTo(size, -size * 0.2)
    ctx.lineTo(size * 0.35, size)
    ctx.lineTo(-size * 0.8, size * 0.55)
    ctx.closePath()
    ctx.fill()
    ctx.restore()
  }
  ctx.restore()
}

// 揺らぐ柱 (炎の舌 / 煙柱) を1枚の多角形で。width/height はピクセル、sway は先端の横ぶれ。
function tonguePath(ctx: CanvasRenderingContext2D, cx: number, baseY: number, width: number, height: number, sway: number, seed: number, timeSec: number): void {
  const n = 7
  ctx.beginPath()
  ctx.moveTo(cx - width / 2, baseY + 2)
  for (let i = 1; i <= n; i += 1) {
    const u = i / n
    const taper = 1 - u * 0.75
    const wob = Math.sin(timeSec * 22 + u * 9 + seed) * width * 0.16 * u
    ctx.lineTo(cx - width / 2 * taper + wob + sway * u * u, baseY - height * u)
  }
  for (let i = n; i >= 1; i -= 1) {
    const u = i / n
    const taper = 1 - u * 0.75
    const wob = Math.sin(timeSec * 19 + u * 7 + seed + 2) * width * 0.16 * u
    ctx.lineTo(cx + width / 2 * taper + wob + sway * u * u, baseY - height * u)
  }
  ctx.lineTo(cx + width / 2, baseY + 2)
  ctx.closePath()
}

// ───────────────────────── 予兆 (telegraph) ─────────────────────────

// 描いた警告 (10-30 から配布版。前の光る筋は ?execCrack=glow)。床の染み・育つひび・漏れる土ぼこり・跳ねる小石。
function drawDrawnWarning(ctx: CanvasRenderingContext2D, b: Band, pre: number, late: boolean, timeSec: number): void {
  const deepen = late ? 0.9 + 0.1 * Math.sin(timeSec * 14) : 1
  // 床の染み: 帯の床が赤黒く沈む (暗くするだけ・発光しない)。
  floorEllipse(ctx, b.cx, b.floorY - 1, b.w * 0.62, 10, 'rgba(58,6,12,1)', (0.30 + 0.40 * pre) * deepen, 'source-over')
  // 育つひび: 打点側の縁から外へ、段0→2 の絵を伸ばす。暗い裂け目の上に沈んだ赤を薄く重ねる。
  const stage = Math.min(2, Math.floor(pre * 3))
  const img = crackSprite(stage)
  if (img) {
    const flip = b.dir < 0
    const w = b.w * 1.05
    const h = CRACK_H_PX * 0.8
    drawMat(ctx, tinted(img, '12,6,8'), b.inner, b.floorY + 1, w, h * 1.1, 0, 0.5, 0.35 + 0.5 * pre, 0, flip)
    drawMat(ctx, tinted(img, '120,18,26'), b.inner, b.floorY + 1, w, h, 0, 0.5, (0.25 + 0.45 * pre) * deepen, 0, flip)
  }
  // 漏れる土ぼこり: 継ぎ目の位置から描いた砂煙の粒が低く立って消える。
  for (let i = 0; i < 3; i += 1) {
    const life = (timeSec * (0.8 + hash01(b.seed + i * 2.9) * 0.5) + hash01(b.seed + i)) % 1
    const bit = dustSprite('bit', i + Math.floor(timeSec * 3 + b.seed))
    if (!bit) continue
    const x = b.xStart + b.w * ((i + 1) / 4) + (hash01(b.seed + i * 1.7) - 0.5) * 8
    const s = 18 + 14 * life + 10 * pre
    drawMat(ctx, bit, x, b.floorY - life * (8 + 10 * pre), s, s * bit.naturalHeight / bit.naturalWidth, 0.5, 1, (0.25 + 0.35 * pre) * Math.sin(Math.PI * life))
  }
  drawHoppingPebbles(ctx, b, 0.4 + 0.8 * pre, timeSec, 7)
}

export function drawQuakeTelegraph(
  ctx: CanvasRenderingContext2D, hz: ActiveHit, timeSec: number, fx: QuakeFxContext, look: QuakeLook,
): void {
  const b = bandOf(hz, fx)
  const t = hz.elapsedMs
  const pre = clamp01(t / Math.max(1, b.slamAtMs))       // 突き立てまでの進み
  const late = pre >= 0.72                                  // 突き立て直前
  const pulse = late ? 0.8 + 0.2 * Math.sin(timeSec * 16) : 1
  ctx.save()
  if (look === 'a' && crackLook === 'crack' && crackReady() && dustReady()) {
    // 比較版の警告 (10-30・作者 exec-fx「床の警告がネオンのまま」): 光を足さず、床そのものが変わる形で示す。
    //   帯の床が赤黒く染みて、ひびの絵が打点側の縁から育ち、継ぎ目から土ぼこりが漏れ、小石が跳ねる。全部ふつうの重ね (lighter を使わない)。
    drawDrawnWarning(ctx, b, pre, late, timeSec)
    drawRacingFissure(ctx, b, t, { glow: 0.3, hot: false, smoke: false, timeSec })
  } else if (look === 'a') {
    // a: 灼熱の地脈 — 床の下で熱が育つ。面の赤い滲み + 継ぎ目から漏れる光 + 火の粉。
    floorEllipse(ctx, b.cx, b.floorY - 1, b.w * 0.66, 11, 'rgba(176,24,34,1)', (0.22 + 0.36 * pre) * pulse, 'lighter')
    drawSeamBloom(ctx, b, (0.30 + 0.5 * pre) * pulse, timeSec, 9 + 7 * pre)
    if (pre > 0.2) emberRise(ctx, b.cx, b.floorY - 2, 4, MOLTEN, timeSec, b.seed, 26 + 18 * pre, b.w * 0.55)
    drawRacingFissure(ctx, b, t, { glow: 1, hot: true, smoke: false, timeSec })
  } else if (look === 'b') {
    // b: 石牙 — 石畳が軋んで浮く。小石が跳ね、継ぎ目から埃が染み出し、赤は継ぎ目の奥に少しだけ。
    const lift = 3 + 8 * easeIn(pre)
    drawSlabs(ctx, b, lift, 0.95)
    drawSeamGlow(ctx, b, (0.22 + 0.4 * pre) * pulse, timeSec, 3 + 5 * pre)
    drawDustSeep(ctx, b, 0.7 + 0.6 * pre, timeSec, 18 + 14 * pre)
    drawHoppingPebbles(ctx, b, 0.5 + 0.8 * pre, timeSec, 8)
    const race = drawRacingFissure(ctx, b, t, { glow: 0.55, hot: false, smoke: false, timeSec })
    if (race.covered > 0) drawSlabs(ctx, b, lift + 4 * race.covered, 0.9)
  } else {
    // c: 呪いの噴出 — 床が黒ずみ、赤黒い靄が継ぎ目から滲む。突き立て直前に靄が濃くなる。
    floorEllipse(ctx, b.cx, b.floorY - 1, b.w * 0.72, 10, 'rgba(8,3,6,1)', 0.45 * pre + 0.25, 'source-over')
    ctx.globalCompositeOperation = 'source-over'
    for (let i = 0; i < 7; i += 1) {
      const life = (timeSec * (0.35 + hash01(b.seed + i * 3.1) * 0.3) + hash01(b.seed + i)) % 1
      const x = b.xStart + b.w * ((i + 0.5) / 7) + Math.sin(timeSec * 1.5 + i) * 4
      const y = b.floorY - 3 - life * (22 + 18 * pre)
      const r = 7 + life * 10
      ctx.globalAlpha = (0.32 + 0.35 * pre) * (1 - life) * pulse
      ctx.fillStyle = 'rgb(46,6,14)'
      ctx.beginPath()
      ctx.arc(x, y, r, 0, Math.PI * 2)
      ctx.fill()
      glowMote(ctx, x, y, r * 0.6, CURSE_MID, (0.16 + 0.26 * pre) * (1 - life))
    }
    drawSeamGlow(ctx, b, (0.28 + 0.45 * pre) * pulse, timeSec, 4 + 5 * pre)
    if (late) emberRise(ctx, b.cx, b.floorY - 2, 2, CURSE_BRIGHT, timeSec, b.seed + 9, 26, b.w * 0.4)
    drawRacingFissure(ctx, b, t, { glow: 0.8, hot: false, smoke: true, timeSec })
  }
  ctx.restore()
}

// ───────────────────────── 噴出 (active) ─────────────────────────

/** 開ききった亀裂 (帯の全幅) だけを描く。介錯の石柱 (src/exec_floor_fx.ts) が柱の根元に使う。 */
export function drawQuakeOpenFissure(ctx: CanvasRenderingContext2D, hz: ActiveHit, timeSec: number, fx: QuakeFxContext, glow: number): void {
  if (glow <= 0.01) return
  ctx.save()
  drawRacingFissure(ctx, bandOf(hz, fx), hz.telegraphMs + 100000, { glow, hot: true, smoke: false, timeSec })
  ctx.restore()
}

export function drawQuakeActive(
  ctx: CanvasRenderingContext2D, hz: ActiveHit, timeSec: number, fx: QuakeFxContext, look: QuakeLook,
): void {
  const b = bandOf(hz, fx)
  const ae = hz.elapsedMs - hz.telegraphMs          // 噴出してからの ms
  const total = Math.max(1, hz.activeMs)
  const ap = clamp01(ae / total)
  const ageSec = ae / 1000
  // 立ち上がり→保持→沈む (3案共通の拍。判定 260ms に合わせる)
  const rise = ae < 70 ? easeOut(ae / 70) : ae < 170 ? 1 : 1 - easeIn((ae - 170) / Math.max(1, total - 170))
  const fadeTail = ap > 0.85 ? (1 - ap) / 0.15 : 1
  ctx.save()
  // 開ききった亀裂 (帯の全幅)
  drawRacingFissure(ctx, b, hz.telegraphMs + 100000, {
    glow: look === 'b' ? 0.5 * fadeTail : (0.7 + 0.3 * (1 - ap)) * fadeTail, hot: look === 'a', smoke: look === 'c', timeSec,
  })

  if (look === 'a') {
    // a: 溶けた光の舌が縦に噴き上がる。外=呪いの赤・中=橙・芯=白熱。石片は黒い影で飛ぶ。
    const H = 132 * rise
    const sway = Math.sin(timeSec * 13 + b.seed) * 10
    ctx.globalCompositeOperation = 'lighter'
    // 🔵 第6便「線っぽいのはダサい」: 細長い白熱の芯 (線に見えた) を外し、幅のある3層 + 根元と胴の光の溜まり (粒) で作る。
    const layers: Array<[string, number, number, number]> = [
      [CURSE_DEEP, 1.0, 1.0, 0.7],
      [CURSE_MID, 0.74, 0.88, 0.7],
      [MOLTEN, 0.48, 0.66, 0.7],
    ]
    for (const [color, wMul, hMul, a] of layers) {
      ctx.globalAlpha = a * fadeTail
      ctx.fillStyle = color
      tonguePath(ctx, b.cx, b.floorY, b.w * 0.68 * wMul, H * hMul, sway * (1.2 - wMul), b.seed, timeSec)
      ctx.fill()
    }
    // 芯の熱は「溜まり」で: 根元に大きく、胴の 1/3 に小さく (線にしない)
    glowMote(ctx, b.cx + sway * 0.1, b.floorY - H * 0.12, 16 * rise, MOLTEN, 0.7 * fadeTail)
    glowMote(ctx, b.cx + sway * 0.1, b.floorY - H * 0.12, 8 * rise, WHITE_HOT, 0.55 * fadeTail)
    glowMote(ctx, b.cx + sway * 0.35, b.floorY - H * 0.4, 9 * rise, MOLTEN, 0.45 * fadeTail)
    // 根元の面の光
    floorEllipse(ctx, b.cx, b.floorY - 1, b.w * 0.8, 12, 'rgba(255,138,74,1)', 0.45 * rise * fadeTail, 'lighter')
    // 火の粉と石片
    emberBurst(ctx, b.cx, b.floorY - 6, 14, MOLTEN, ageSec, 0.42, b.seed, 60, 220)
    drawShards(ctx, b, clamp01(ae / 200), 9, 90, '#1a181c', '#2a262b')
  } else if (look === 'b') {
    // b: 石の尖塔が3本、裂け目から突き上がる。面は左が明るく右が暗い。赤は根元の隙間だけ。
    const spires: Array<[number, number, number]> = [[-0.3, 0.72, 0.8], [0.05, 1.0, 1.0], [0.34, 0.66, 0.9]]
    // 根元の埃 (先に描いて塔を手前に)
    for (let i = 0; i < 4; i += 1) {
      const dp = clamp01(ae / 140)
      const x = b.cx + (i - 1.5) * b.w * 0.26
      const y = b.floorY - 8 - dp * (18 + hash01(b.seed + i) * 20)
      const r = 16 + hash01(b.seed + i * 2.2) * 10
      ctx.globalCompositeOperation = 'source-over'
      ctx.globalAlpha = 0.5 * (1 - ap) * fadeTail
      const g = ctx.createRadialGradient(x, y, 0, x, y, r)
      g.addColorStop(0, 'rgba(150,144,136,0.75)')
      g.addColorStop(1, 'rgba(100,96,92,0)')
      ctx.fillStyle = g
      ctx.beginPath()
      ctx.arc(x, y, r, 0, Math.PI * 2)
      ctx.fill()
    }
    ctx.globalCompositeOperation = 'source-over'
    for (const [off, hMul, wMul] of spires) {
      const H = 126 * hMul * rise * (0.92 + 0.16 * hash01(b.seed + off))
      if (H < 2) continue
      const x = b.cx + off * b.w
      const half = 13 * wMul
      const lean = b.dir * (5 + 5 * hash01(b.seed + off * 3)) * rise
      const tipX = x + lean
      const tipY = b.floorY - H
      const ridgeX = x + lean * 0.5 - half * 0.15
      ctx.globalAlpha = fadeTail
      // 明るい面 (左)
      ctx.fillStyle = '#75706a'
      ctx.beginPath()
      ctx.moveTo(x - half, b.floorY + 1)
      ctx.lineTo(x - half * 0.7, b.floorY - H * 0.55)
      ctx.lineTo(tipX, tipY)
      ctx.lineTo(ridgeX, b.floorY - H * 0.35)
      ctx.lineTo(x - half * 0.1, b.floorY + 1)
      ctx.closePath()
      ctx.fill()
      // 暗い面 (右)
      ctx.fillStyle = '#39363a'
      ctx.beginPath()
      ctx.moveTo(x - half * 0.1, b.floorY + 1)
      ctx.lineTo(ridgeX, b.floorY - H * 0.35)
      ctx.lineTo(tipX, tipY)
      ctx.lineTo(x + half * 0.75, b.floorY - H * 0.5)
      ctx.lineTo(x + half, b.floorY + 1)
      ctx.closePath()
      ctx.fill()
      // 輪郭と稜線
      ctx.strokeStyle = '#121214'
      ctx.lineWidth = 1.2
      ctx.beginPath()
      ctx.moveTo(x - half, b.floorY + 1)
      ctx.lineTo(x - half * 0.7, b.floorY - H * 0.55)
      ctx.lineTo(tipX, tipY)
      ctx.lineTo(x + half * 0.75, b.floorY - H * 0.5)
      ctx.lineTo(x + half, b.floorY + 1)
      ctx.stroke()
      ctx.globalAlpha = 0.5 * fadeTail
      ctx.strokeStyle = '#9a948b'
      ctx.lineWidth = 0.8
      ctx.beginPath()
      ctx.moveTo(ridgeX, b.floorY - H * 0.35)
      ctx.lineTo(tipX, tipY)
      ctx.stroke()
    }
    // 根元の赤い隙間 (弾けない合図) と石片
    drawSeamGlow(ctx, b, 0.55 * (1 - ap) * fadeTail, timeSec, 6)
    drawShards(ctx, b, clamp01(ae / 190), 10, 70, '#716b64', '#4e4a47')
  } else {
    // c: 赤黒い呪いの煙柱。黒い煙を先に、その縁に赤い光。根元に一瞬の閃光。
    const H = 150 * rise
    const sway = Math.sin(timeSec * 9 + b.seed) * 14
    ctx.globalCompositeOperation = 'source-over'
    ctx.globalAlpha = 0.72 * fadeTail
    ctx.fillStyle = 'rgb(22,6,11)'
    tonguePath(ctx, b.cx, b.floorY, b.w * 0.62, H, sway, b.seed, timeSec)
    ctx.fill()
    ctx.globalCompositeOperation = 'lighter'
    const layers: Array<[string, number, number, number]> = [
      [CURSE_DEEP, 0.66, 0.96, 0.7],
      [CURSE_MID, 0.36, 0.82, 0.6],
      [CURSE_BRIGHT, 0.14, 0.6, 0.55],
    ]
    for (const [color, wMul, hMul, a] of layers) {
      ctx.globalAlpha = a * fadeTail
      ctx.fillStyle = color
      tonguePath(ctx, b.cx, b.floorY, b.w * 0.62 * wMul, H * hMul, sway * (1.3 - wMul), b.seed + 5, timeSec)
      ctx.fill()
    }
    // 根元の閃光 (最初の 60ms) — 帯の幅いっぱいの細い光の線
    if (ae < 60) {
      ctx.globalAlpha = (1 - ae / 60) * 0.9
      ctx.strokeStyle = 'rgb(255,120,110)'
      ctx.lineWidth = 3
      ctx.beginPath()
      ctx.moveTo(b.xStart - 6, b.floorY - 1)
      ctx.lineTo(b.xEnd + 6, b.floorY - 1)
      ctx.stroke()
    }
    floorEllipse(ctx, b.cx, b.floorY - 1, b.w * 0.8, 11, 'rgba(176,24,34,1)', 0.4 * rise * fadeTail, 'lighter')
    emberBurst(ctx, b.cx, b.floorY - 6, 12, CURSE_BRIGHT, ageSec, 0.5, b.seed + 3, 50, 180)
    drawShards(ctx, b, clamp01(ae / 220), 7, 60, '#17161a', '#262329')
  }
  ctx.restore()
}
